import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import ky, { SearchParamsOption } from "ky";
import { useContext } from "react";
import { decryptObject, E2E_IV } from "../helpers/e2e";
import { getStatusCode } from "../helpers/errors";
import HourglassGlobals, { HGContext } from "../helpers/globals";
import { addShareTokenToCache, removeShareTokenFromCache, sortAndLabelAddresses } from "../query/territory";
import Address from "../types/address";
import { DeleteStatus, UpdateStatus } from "../types/scheduling/meetings";
import {
  SharedTerritoryResponse,
  Territory,
  TerritoryAddress,
  TerritoryAddressSortOrder,
  TerritoryAddressStatistics,
  TerritoryAddressVisit,
  TerritoryBulkCheckinResponse,
  TerritoryCong,
  TerritoryImport,
  TerritoryMoveAddresses,
  TerritoryRecord,
  TerritoryRequest,
  TerritoryShareToken,
  TerritoryShareTokenListing,
  TerritoryTransfer,
  TerritoryTransferRequest,
  UserTerritory,
} from "../types/scheduling/territory";
import { api, schedulingPath } from "./api";
import { baseUrl, UnknownCharacter } from "./const";
import { ImportAPI } from "./import";
import QueryKeys from "./queryKeys";
import { parseLocation } from "./helpers";

const TerritoryShareTokenHeader = "X-Territory-Share-Token";

function createShareApi(token: string) {
  return ky.create({
    prefixUrl: baseUrl,
    timeout: 20 * 1000,
    hooks: {
      beforeRequest: [
        (request) => {
          request.headers.set(TerritoryShareTokenHeader, token);
        },
      ],
    },
  });
}

export const terrAddressesPerPage = 1000;

class TerritoryAPI {
  base = schedulingPath("/territory");

  static async addressFromResponse(addr: any): Promise<TerritoryAddress> {
    if (HourglassGlobals.e2eKey && addr[E2E_IV]) {
      try {
        addr = await decryptObject(addr, HourglassGlobals.e2eKey);
        if (addr.location) {
          // decrypted, it will just be a string
          const locStr = addr.location as string;
          addr.location = parseLocation(locStr);
        }
      } catch (err) {
        console.error("failure to decrypt territory address", addr.id, err);
      }
      if (!addr.line1) addr.line1 = UnknownCharacter;
    }

    return addr;
  }

  async getTerritories(): Promise<Territory[]> {
    return api.get(this.base).json();
  }

  async getTerritory(id: number): Promise<Territory> {
    const url = `${this.base}/${id}`;
    return api.get(url).json();
  }

  async upsertOneTerritory(terr: Territory): Promise<Territory> {
    return api.put(this.base, { json: terr }).json();
  }

  async uploadBulkTerritories(formData: FormData): Promise<UpdateStatus> {
    const url = `${this.base}/import/xlsx`;

    try {
      await api.post(url, { body: formData }).json();
      return { Updated: true };
    } catch {
      return { Updated: false };
    }
  }

  async deleteOneTerritory(id: number): Promise<DeleteStatus> {
    const url = `${this.base}/${id}`;
    return api.delete(url).json();
  }

  async deleteTerritories(ids: number[]): Promise<{ successful: number[]; failed: { id: number; error: string }[] }> {
    const url = `${this.base}/bulk/delete`;
    return api.post(url, { json: { ids } }).json();
  }

  async bulkCheckin(ids: number[]): Promise<TerritoryBulkCheckinResponse> {
    const url = `${this.base}/bulk/checkin`;
    return api.post(url, { json: { ids } }).json();
  }

  async getRecords(latest_only: boolean): Promise<TerritoryRecord[]> {
    const url = `${this.base}/records`;
    return api.get(url, { searchParams: { latest_only: latest_only.toString() } }).json();
  }

  async upsertOneRecord(terr: Omit<TerritoryRecord, "congregation_id">): Promise<TerritoryRecord> {
    const url = `${this.base}/records`;
    return api.put(url, { json: terr }).json();
  }

  async deleteOneRecord(id: number): Promise<DeleteStatus> {
    const url = `${this.base}/records/${id}`;
    return api.delete(url).json();
  }

  async sendReminders(ids: number[]) {
    const url = `${this.base}/records/reminders`;
    await api.post(url, { json: ids });
  }

  async getRequests(): Promise<TerritoryRequest[]> {
    const url = `${this.base}/requests`;
    return api.get(url).json();
  }

  async getMyTerritories(): Promise<UserTerritory[]> {
    const url = `${this.base}/my/territories`;
    return api.get(url).json();
  }

  async getMyRequest(): Promise<TerritoryRequest> {
    const url = `${this.base}/my/request`;
    return api.get(url).json();
  }

  async deleteRequest(id: number): Promise<DeleteStatus> {
    const url = `${this.base}/my/request/${id}`;
    return api.delete(url).json();
  }

  async getAddresses(territoryId: number): Promise<TerritoryAddress[]> {
    const url = `${this.base}/addresses/${territoryId}`;
    const data = await api.get(url).json<any>();
    const addresses = await Promise.all(data.map((a: TerritoryAddress) => TerritoryAPI.addressFromResponse(a)));
    return sortAndLabelAddresses(addresses);
  }

  async saveAddress(addr: TerritoryAddress): Promise<TerritoryAddress> {
    if (!addr.territoryId) throw new Error("territory addresses require a territory ID");
    const url = `${this.base}/addresses`;

    const data = await api.put(url, { json: addr }).json();
    return await TerritoryAPI.addressFromResponse(data);
  }

  async updateAddresses(territoryId: number, sortOrder: TerritoryAddressSortOrder) {
    const url = `${this.base}/${territoryId}/addressSortOrder`;
    await api.post(url, { json: Object.fromEntries(sortOrder) });
  }

  async deleteAddress(addressId: number) {
    const url = `${this.base}/addresses/${addressId}`;
    await api.delete(url);
  }

  async saveVisit(visit: TerritoryAddressVisit): Promise<TerritoryAddressVisit> {
    const url = `${this.base}/addresses/visits`;
    const method = visit.id ? api.put : api.post;
    return method(url, { json: visit }).json();
  }

  async deleteVisit(visitId: number): Promise<void> {
    const url = `${this.base}/addresses/visits/${visitId}`;
    await api.delete(url);
  }

  async deleteAllVisitsByAddress(addressId: number): Promise<void> {
    const url = `${this.base}/addresses/visits/address/${addressId}`;
    await api.delete(url);
  }

  async deleteAllVisitsByTerritory(territoryId: number): Promise<void> {
    const url = `${this.base}/addresses/visits/territory/${territoryId}`;
    await api.delete(url);
  }

  async deleteTerritoryAddresses(territoryId: number) {
    const url = `${this.base}/addresses/all/${territoryId}`;
    await api.delete(url);
  }

  async resetAddressStatus(territoryId: number): Promise<TerritoryAddress[]> {
    const url = `${this.base}/addresses/status/${territoryId}`;
    const data = await api.delete(url).json<any>();
    const addresses = await Promise.all(data.map((a: TerritoryAddress) => TerritoryAPI.addressFromResponse(a)));
    return sortAndLabelAddresses(addresses);
  }

  async getAllAddresses(cursor: number): Promise<TerritoryAddress[]> {
    const query = cursor > 0 ? { cursor: cursor } : undefined;
    const url = `${this.base}/addresses/all`;
    return await api.get(url, { searchParams: query }).json();
  }

  async locateAddress(address: string): Promise<TerritoryAddress> {
    return api.get(`${this.base}/geo/point`, { searchParams: { address: address } }).json();
  }

  async searchAddress(addressQuery: string, tagIds: number[], dnc: boolean): Promise<TerritoryAddress[]> {
    const params: SearchParamsOption = {};
    if (addressQuery) params.address = addressQuery;
    if (tagIds.length) params.tagIds = tagIds.join(",");
    if (dnc) params.dnc = true;

    return api.get(`${this.base}/addresses/search`, { searchParams: params }).json();
  }

  async moveAddresses(moveAddrs: TerritoryMoveAddresses) {
    const url = `${this.base}/addresses/move`;
    await api.put(url, { json: moveAddrs });
  }

  async setCongTerritory(congTerr: TerritoryCong): Promise<TerritoryCong> {
    const url = `${this.base}/cong/geojson`;
    return api.put(url, { json: congTerr }).json();
  }

  async getCongTerritory(): Promise<TerritoryCong> {
    const url = `${this.base}/cong/geojson`;
    return api.get(url).json();
  }

  async getAddressFromPoint(lat: number, lng: number): Promise<Address> {
    const url = `${this.base}/geo/address`;
    return api.get(url, { searchParams: { lat: lat, lng: lng } }).json();
  }

  async importNative(input: TerritoryImport) {
    const url = `${this.base}/import/native`;
    return api.post(url, { json: input, timeout: ImportAPI.importTimeout }).json();
  }

  async getAddressStatistics(): Promise<TerritoryAddressStatistics> {
    const url = `${this.base}/addresses/stats`;
    return api.get(url).json();
  }

  shareBase = schedulingPath("/territory/share");

  async getSharedTerritory(token: string): Promise<SharedTerritoryResponse> {
    const shareApi = createShareApi(token);
    return shareApi.get(`${this.shareBase}/${token}`).json<SharedTerritoryResponse>();
  }

  async getShareTokensForTerritory(territoryId: number): Promise<TerritoryShareTokenListing[]> {
    return api.get(`${this.shareBase}/tokens/${territoryId}`).json<TerritoryShareTokenListing[]>();
  }

  async createShareToken(territoryId: number): Promise<TerritoryShareToken> {
    return api.post(this.shareBase, { json: { territory_id: territoryId } }).json<TerritoryShareToken>();
  }

  async revokeShareToken(tokenHash: string): Promise<unknown> {
    return api.delete(`${this.shareBase}/${tokenHash}`).json();
  }

  transferBase = schedulingPath("/territory/transfer");

  async getTerritoryTransfers(): Promise<TerritoryTransfer[]> {
    return api.get(this.transferBase).json();
  }

  async saveTerritoryTransfer(req: TerritoryTransferRequest): Promise<TerritoryTransfer> {
    const url = req.id ? `${this.transferBase}/${req.id}` : this.transferBase;
    const method = req.id ? "put" : "post";
    return api(url, { method, json: req }).json();
  }

  async deleteTerritoryTransfer(id: number): Promise<void> {
    await api.delete(`${this.transferBase}/${id}`);
  }

  async previewTerritoryTransfer(code: string): Promise<TerritoryTransfer> {
    return api.post(`${this.transferBase}/preview`, { json: { code } }).json();
  }

  async completeTerritoryTransfer(code: string): Promise<TerritoryTransfer> {
    return api.post(`${this.transferBase}/complete`, { json: { code } }).json();
  }
}

export function useCongTerritory(options?: Partial<UseQueryOptions<TerritoryCong, Error>>) {
  const ctx = useContext(HGContext);

  const resp = useQuery({
    queryKey: [QueryKeys.TerritoryCong],
    queryFn: () => territoryApi.getCongTerritory(),
    ...options,

    // this changes so infrequently that we can cache it for the duration of a session
    gcTime: Infinity,
    staleTime: Infinity,
    enabled: !ctx.globals.missingCongTerritory && !!options?.enabled,

    retry: (failureCount, err) => {
      switch (getStatusCode(err)) {
        case 404:
          ctx.setGlobals({ ...ctx.globals, missingCongTerritory: true });
          return false;
      }
      return failureCount < 2;
    },
  });

  if (resp.isSuccess && resp.data && ctx.globals.missingCongTerritory) {
    ctx.setGlobals({ ...ctx.globals, missingCongTerritory: false });
  }

  return resp;
}

export function useTerritoryRecords(latestOnly: boolean, options?: UseQueryOptions<TerritoryRecord[], Error>) {
  return useQuery({
    queryKey: [latestOnly ? QueryKeys.TerritoryRecordsLatest : QueryKeys.TerritoryRecordsAll],
    queryFn: () => territoryApi.getRecords(latestOnly),
    ...options,
  });
}

export function useTerritories(options?: Partial<UseQueryOptions<Territory[], Error>>) {
  return useQuery({
    queryKey: [QueryKeys.Territories],
    queryFn: () => territoryApi.getTerritories(),
    ...options,
  });
}

export function useTerritory(territoryId: number, options?: Partial<UseQueryOptions<Territory, Error>>) {
  return useQuery({
    queryKey: [QueryKeys.Territory, territoryId],
    queryFn: () => territoryApi.getTerritory(territoryId),
    ...options,
  });
}

export function useTerritoryAddressStatistics(options?: Partial<UseQueryOptions<TerritoryAddressStatistics, Error>>) {
  return useQuery({
    queryKey: [QueryKeys.TerritoryAddressStats],
    queryFn: () => territoryApi.getAddressStatistics(),
    ...options,
  });
}

export function useMyTerritories(options?: UseQueryOptions<UserTerritory[], Error>) {
  return useQuery({
    queryKey: [QueryKeys.MyTerritories],
    queryFn: () => territoryApi.getMyTerritories(),
    ...options,
  });
}

export const territoryApi = new TerritoryAPI();

export function useSharedTerritory(token: string) {
  return useQuery({
    queryKey: [QueryKeys.SharedTerritory, token],
    queryFn: () => territoryApi.getSharedTerritory(token),
    enabled: !!token,
    retry: false,
  });
}

export function useShareTokensForTerritory(territoryId: number, enabled = true) {
  return useQuery({
    queryKey: [QueryKeys.SharedTerritoryTokens, territoryId],
    queryFn: () => territoryApi.getShareTokensForTerritory(territoryId),
    enabled: !!territoryId && enabled,
  });
}

export function useCreateShareToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (territoryId: number) => territoryApi.createShareToken(territoryId),
    onSuccess: (data) => {
      addShareTokenToCache(queryClient, data);
    },
  });
}

export function useTerritoryTransfers(options?: Partial<UseQueryOptions<TerritoryTransfer[], Error>>) {
  return useQuery({
    queryKey: [QueryKeys.TerritoryTransfers],
    queryFn: () => territoryApi.getTerritoryTransfers(),
    ...options,
  });
}

export function useRevokeShareToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tokenHash, territoryId: _territoryId }: { tokenHash: string; territoryId: number }) =>
      territoryApi.revokeShareToken(tokenHash),
    onSuccess: (_data, variables) => {
      removeShareTokenFromCache(queryClient, variables.tokenHash, variables.territoryId);
    },
  });
}
